import { useReglages } from '../../api/reglages.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { tr } from '../../lib/i18n';
import {
  CLE_POINTAGE_ATELIER,
  CLE_POINTAGE_HEURES,
  CLE_POINTAGE_TRAVAUX,
  reglagesPointage,
} from '../../lib/reglages-pointage';
import { C } from '../../theme/palette';

/** Un choix parmi plusieurs, en grandes cases. */
function Choix({ titre, options, valeur, onChange }) {
  return (
    <div role="radiogroup" aria-label={titre} style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, color: C.ink, marginBottom: 6 }}>{titre}</div>
      {options.map(([v, libelle, aide]) => {
        const on = valeur === v;
        return (
          <div
            key={v}
            role="radio"
            aria-checked={on}
            aria-label={libelle}
            onClick={() => onChange(v)}
            style={{
              border: '2px solid ' + (on ? C.navy : C.border),
              background: on ? C.navyXL : C.white,
              borderRadius: 11,
              padding: '9px 11px',
              marginBottom: 6,
              cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 800, color: C.ink }}>
              {on ? '● ' : '○ '}
              {libelle}
            </div>
            <div style={{ fontSize: 9, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>{aide}</div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Réglages → Pointage des employés : le patron choisit si les heures sont
 * automatiques ou manuelles, et comment l'employé note ce qu'il a fait.
 *
 * @returns {import('react').ReactElement} la carte
 */
export function PointageCard({ magasin = false } = {}) {
  const { reglage, setReglage } = useReglages();
  const { heures, travaux, atelier } = reglagesPointage(reglage);
  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar
        icon="⏱"
        title={tr('Pointage des employés')}
        color={C.amber}
        sub={tr('Comment tes employés notent leurs heures et leur travail')}
      />
      <div style={{ padding: 13 }}>
        <Choix
          titre={tr('Heures d’arrivée et de départ')}
          valeur={heures}
          onChange={(v) => setReglage(CLE_POINTAGE_HEURES, v)}
          options={[
            [
              'auto',
              tr('⏱ Automatique'),
              tr('L’heure du téléphone au moment où il pointe. L’employé ne peut pas la changer.'),
            ],
            [
              'manuel',
              tr('✍️ Manuel'),
              tr(
                'L’employé peut corriger son heure d’arrivée et de départ (arrivé plus tôt, oubli…).',
              ),
            ],
          ]}
        />
        {/* Au magasin, le pointage n'a pas de rapport des travaux du jour. */}
        {!magasin && (
          <Choix
            titre={tr('Ce qu’il a fait dans la journée')}
            valeur={travaux}
            onChange={(v) => setReglage(CLE_POINTAGE_TRAVAUX, v)}
            options={[
              [
                'postes',
                tr('☑️ Cocher les postes de l’offre'),
                tr('Il choisit dans la liste des travaux de l’offre du chantier.'),
              ],
              [
                'texte',
                tr('✍️ Il l’écrit lui-même'),
                tr(
                  'Il écrit ce qu’il a fait, à sa façon. Pratique pour un menuisier sur plusieurs meubles ou chantiers.',
                ),
              ],
              [
                'les_deux',
                tr('☑️ + ✍️ Les deux'),
                tr('Il coche les postes et peut ajouter un texte.'),
              ],
            ]}
          />
        )}
        {/* Un magasin n'a ni chantier ni atelier : le choix ne le concerne pas. */}
        {!magasin && (
          <Choix
            titre={tr('Travail à l’atelier')}
            valeur={atelier ? 'oui' : 'non'}
            onChange={(v) => setReglage(CLE_POINTAGE_ATELIER, v)}
            options={[
              ['non', tr('🏗 Seulement sur les chantiers'), tr('L’employé pointe sur un chantier.')],
              [
                'oui',
                tr('🏭 Chantier ou atelier'),
                tr(
                  'L’employé dit où il travaille : sur le chantier, à l’atelier pour un chantier (meubles fabriqués avant la pose), ou à l’atelier en général.',
                ),
              ],
            ]}
          />
        )}
      </div>
    </Card>
  );
}
